/**
 * МОСТ ШАРИНГА В НАТИВНУЮ ОБОЛОЧКУ (Android/Capacitor), 2026-10-05.
 *
 * ─── ЗАЧЕМ ──────────────────────────────────────────────────────────────────
 * Замер (Антон, долг с 27.09; подтверждён повторным замером 05.10):
 * в Android WebView оболочки `alg/messenger-android` `navigator.share`
 * ОТСУТСТВУЕТ — кнопки «Поделиться» в вебе просто не рисовались, и шаринга
 * на телефоне не было вовсе. Системный шаринг на Android умеет делать только
 * НАТИВНЫЙ код (`Intent.ACTION_SEND` / `ACTION_SEND_MULTIPLE`), поэтому без
 * моста обойтись нельзя.
 *
 * ─── КАКОЙ МОСТ ─────────────────────────────────────────────────────────────
 * Мост Capacitor УЖЕ существует и используется для пушей
 * (`src/notifications/nativePush.ts`): нативная часть впрыскивает глобал
 * `window.Capacitor` с `isNativePlatform()`, `isPluginAvailable(name)` и
 * реестром `Plugins`. Второй мост НЕ заводим — шаринг идёт тем же путём,
 * через СТАНДАРТНЫЕ плагины Capacitor:
 *
 *   • `Share` (`@capacitor/share`) — системное меню «Поделиться».
 *     Контракт вызова (совпадает с официальным плагином):
 *       `Capacitor.Plugins.Share.share({ title?, text?, url?, files? })`
 *       → Promise; отказ пользователя = rejected promise (сообщение
 *       "Share canceled" / AbortError-подобное) — это НЕ ошибка.
 *       `files` — массив СТРОКОВЫХ URI (`file://…`), не Blob.
 *   • `Filesystem` (`@capacitor/filesystem`) — нужен ТОЛЬКО для файлового
 *     шаринга: Blob в WebView нельзя передать в Intent напрямую, поэтому
 *     байты пишем во временный файл кэша и шарим его URI:
 *       `Filesystem.writeFile({ path, data: <base64>, directory: "CACHE", recursive: true })`
 *       `Filesystem.getUri({ path, directory: "CACHE" })` → `{ uri }`
 *       …после шаринга: `Filesystem.deleteFile({ path, directory: "CACHE" })`.
 *
 * ─── ЧТО ДОЛЖНА СДЕЛАТЬ НАТИВНАЯ ПОЛОВИНА (репозиторий
 *     alg/messenger-android — ДРУГОЙ репозиторий, здесь НЕ сделано) ─────────
 *   1. `npm i @capacitor/share @capacitor/filesystem` в нативном проекте
 *      (или добавить плагины иначе) и `npx cap sync android`, чтобы
 *      классы плагинов слинковались в APK. Сторонних разрешений не нужно:
 *      шаринг из CACHE-каталога приложения FileProvider'ом Capacitor
 *      покрывается (плагин share сам подставляет content:// URI).
 *   2. Ничего больше: регистрация плагинов в Capacitor 8 автоматическая.
 * Проверка доступности с веб-стороны — `isPluginAvailable("Share")`
 * (и `"Filesystem"` для файлов), то есть старая сборка оболочки без
 * плагинов НЕ сломается: мост отрапортует недоступность, и веб уйдёт
 * в запасной путь (скачивание / копирование ссылки).
 *
 * ─── БРАУЗЕР / DESKTOP ──────────────────────────────────────────────────────
 * Не нативная оболочка → все функции возвращают «нет моста», ни одна строка
 * сетевого или нативного кода не выполняется. Web Share API и запасные пути
 * решает вызывающий (`src/ui/share.ts`) — здесь только мост.
 */

/** Минимальная типизация глобала Capacitor (полная — в @capacitor/core). */
interface CapacitorShareGlobal {
  isNativePlatform?: () => boolean;
  isPluginAvailable?: (name: string) => boolean;
  Plugins?: {
    Share?: {
      share: (options: {
        title?: string;
        text?: string;
        url?: string;
        files?: string[];
      }) => Promise<unknown>;
    };
    Filesystem?: {
      writeFile: (options: {
        path: string;
        data: string;
        directory: string;
        recursive?: boolean;
      }) => Promise<unknown>;
      getUri: (options: { path: string; directory: string }) => Promise<{ uri: string }>;
      deleteFile: (options: { path: string; directory: string }) => Promise<unknown>;
    };
  };
}

function capacitor(): CapacitorShareGlobal | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: CapacitorShareGlobal }).Capacitor;
  if (!cap) return null;
  try {
    if (cap.isNativePlatform?.() !== true) return null;
  } catch {
    return null;
  }
  return cap;
}

/** «Отменено пользователем» — то же, что AbortError у Web Share: не ошибка. */
export function isShareCanceled(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /abort|cancel/i.test(msg);
}

/** Шаринг ТЕКСТА/ССЫЛКИ через нативное меню. false = моста нет. */
export function canNativeShareText(): boolean {
  const cap = capacitor();
  if (!cap) return false;
  try {
    return cap.isPluginAvailable?.("Share") === true && Boolean(cap.Plugins?.Share?.share);
  } catch {
    return false;
  }
}

/** Шаринг ФАЙЛА через нативное меню: нужен И Share, И Filesystem. */
export function canNativeShareFiles(): boolean {
  const cap = capacitor();
  if (!cap) return false;
  try {
    return (
      cap.isPluginAvailable?.("Share") === true &&
      cap.isPluginAvailable?.("Filesystem") === true &&
      Boolean(cap.Plugins?.Share?.share) &&
      Boolean(cap.Plugins?.Filesystem?.writeFile) &&
      Boolean(cap.Plugins?.Filesystem?.getUri)
    );
  } catch {
    return false;
  }
}

/**
 * Поделиться текстом/ссылкой через нативное меню.
 * Бросает исключение при сбое моста; отмена пользователя — тоже rejected
 * promise, различается через isShareCanceled.
 */
export async function nativeShareText(options: { title?: string; text: string; url?: string }): Promise<void> {
  const share = capacitor()?.Plugins?.Share?.share;
  if (!share) throw new Error("native Share bridge unavailable");
  await share({ title: options.title, text: options.text, url: options.url });
}

function bytesToBase64(bytes: Uint8Array): string {
  // btoa на строке из 8-битных «символов»; chunked, чтобы не упереться в
  // лимит аргументов apply на больших файлах.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Поделиться ФАЙЛОМ через нативное меню: Blob → временный файл в кэше
 * приложения → URI в Share → временный файл удалён (best effort).
 */
export async function nativeShareFile(blob: Blob, name: string, mime: string): Promise<void> {
  const cap = capacitor();
  const fs = cap?.Plugins?.Filesystem;
  const share = cap?.Plugins?.Share?.share;
  if (!fs || !share) throw new Error("native Share/Filesystem bridge unavailable");

  const safeName = name.replace(/[\\/:*?"<>|]/g, "_") || "file";
  const path = `alg-share/${Date.now()}-${safeName}`;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  await fs.writeFile({ path, data: bytesToBase64(bytes), directory: "CACHE", recursive: true });
  try {
    const { uri } = await fs.getUri({ path, directory: "CACHE" });
    await share({ title: name, text: name, files: [uri] });
  } finally {
    // Временный файл — мусор; ошибка удаления не должна маскировать исход шаринга.
    try {
      await fs.deleteFile({ path, directory: "CACHE" });
    } catch {
      /* кэш почистит система */
    }
  }
  void mime;
}
